import React, { useState, useEffect, useCallback } from 'react';
import { tasksApi, dealsApi } from '../lib/api';
import { formatDate, TASK_PRIORITIES, TASK_STATUSES } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Plus, MagnifyingGlass, Pencil, Trash, Calendar, Flag, Circle, CheckCircle
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import logger from '../lib/logger';

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'medium',
    status: 'pending',
    related_deal_id: ''
  });

  const fetchTasks = useCallback(async () => {
    try {
      const params = { limit: 100 };
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;

      const response = await tasksApi.getAll(params);
      setTasks(response.data);
    } catch (error) {
      toast.error('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter]);

  const fetchDeals = useCallback(async () => {
    try {
      const response = await dealsApi.getAll({ limit: 100 });
      setDeals(response.data);
    } catch (error) {
      logger.error('Failed to fetch deals:', error);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    fetchDeals();
  }, [fetchTasks, fetchDeals]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTask) {
        await tasksApi.update(editingTask.id, formData);
        toast.success('Task updated');
      } else {
        await tasksApi.create(formData);
        toast.success('Task created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save task');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await tasksApi.delete(id);
      toast.success('Task deleted');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const toggleTaskStatus = async (task) => {
    const newStatus = task.status === 'completed' ? 'pending' : 'completed';
    try {
      await tasksApi.update(task.id, { ...task, status: newStatus });
      fetchTasks();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const openEditDialog = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      due_date: task.due_date || '',
      priority: task.priority,
      status: task.status,
      related_deal_id: task.related_deal_id || ''
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingTask(null);
    setFormData({
      title: '',
      description: '',
      due_date: '',
      priority: 'medium',
      status: 'pending',
      related_deal_id: ''
    });
  };

  const getPriorityBadge = (priority) => {
    const config = TASK_PRIORITIES.find(p => p.id === priority);
    return (
      <Badge variant="outline" className={config?.color}>
        <Flag size={12} className="mr-1" />
        {config?.label || priority}
      </Badge>
    );
  };

  const isOverdue = (dueDate, status) => {
    if (!dueDate || status === 'completed') return false;
    return new Date(dueDate) < new Date();
  };

  const filteredTasks = tasks.filter(t => 
    !search || t.title.toLowerCase().includes(search.toLowerCase())
  );

  const groupedTasks = {
    overdue: filteredTasks.filter(t => isOverdue(t.due_date, t.status)),
    today: filteredTasks.filter(t => {
      if (!t.due_date || t.status === 'completed') return false;
      const today = new Date().toDateString();
      return new Date(t.due_date).toDateString() === today;
    }),
    upcoming: filteredTasks.filter(t => {
      if (!t.due_date || t.status === 'completed') return false;
      const due = new Date(t.due_date);
      const today = new Date();
      return due > today && due.toDateString() !== today.toDateString();
    }),
    completed: filteredTasks.filter(t => t.status === 'completed'),
    noDueDate: filteredTasks.filter(t => !t.due_date && t.status !== 'completed')
  };

  return (
    <div className="space-y-6" data-testid="tasks-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Tasks</h1>
          <p className="text-muted-foreground text-sm mt-1">{tasks.length} tasks</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setIsDialogOpen(true); }} 
          className="rounded-sm"
          data-testid="add-task-btn"
        >
          <Plus size={18} className="mr-2" />
          Add Task
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input
            placeholder="Search tasks..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 rounded-sm"
            data-testid="search-tasks"
          />
        </div>
        <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="status-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {TASK_STATUSES.map((status) => (
              <SelectItem key={status.id} value={status.id}>{status.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={priorityFilter || "all"} onValueChange={(v) => setPriorityFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="priority-filter">
            <SelectValue placeholder="All Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priority</SelectItem>
            {TASK_PRIORITIES.map((priority) => (
              <SelectItem key={priority.id} value={priority.id}>{priority.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Task Sections */}
      {loading ? (
        <div className="p-8 text-center text-muted-foreground">Loading...</div>
      ) : (
        <div className="space-y-6">
          {/* Overdue */}
          {groupedTasks.overdue.length > 0 && (
            <TaskSection 
              title="Overdue" 
              tasks={groupedTasks.overdue} 
              variant="destructive"
              onToggle={toggleTaskStatus}
              onEdit={openEditDialog}
              onDelete={handleDelete}
            />
          )}

          {/* Today */}
          {groupedTasks.today.length > 0 && (
            <TaskSection 
              title="Today" 
              tasks={groupedTasks.today} 
              variant="warning"
              onToggle={toggleTaskStatus}
              onEdit={openEditDialog}
              onDelete={handleDelete}
            />
          )}

          {/* Upcoming */}
          {groupedTasks.upcoming.length > 0 && (
            <TaskSection 
              title="Upcoming" 
              tasks={groupedTasks.upcoming} 
              onToggle={toggleTaskStatus}
              onEdit={openEditDialog}
              onDelete={handleDelete}
            />
          )}

          {/* No Due Date */}
          {groupedTasks.noDueDate.length > 0 && (
            <TaskSection 
              title="No Due Date" 
              tasks={groupedTasks.noDueDate} 
              onToggle={toggleTaskStatus}
              onEdit={openEditDialog}
              onDelete={handleDelete}
            />
          )}

          {/* Completed */}
          {groupedTasks.completed.length > 0 && (
            <TaskSection 
              title="Completed" 
              tasks={groupedTasks.completed} 
              variant="muted"
              onToggle={toggleTaskStatus}
              onEdit={openEditDialog}
              onDelete={handleDelete}
            />
          )}

          {filteredTasks.length === 0 && (
            <div className="p-8 text-center text-muted-foreground">No tasks found</div>
          )}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTask ? 'Edit Task' : 'Add Task'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                data-testid="task-title"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  data-testid="task-due-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => setFormData({ ...formData, priority: value })}
                >
                  <SelectTrigger data-testid="task-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TASK_PRIORITIES.map((priority) => (
                      <SelectItem key={priority.id} value={priority.id}>{priority.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger data-testid="task-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TASK_STATUSES.map((status) => (
                    <SelectItem key={status.id} value={status.id}>{status.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Related Deal</Label>
              <Select
                value={formData.related_deal_id || "none"}
                onValueChange={(value) => setFormData({ ...formData, related_deal_id: value === "none" ? "" : value })}
              >
                <SelectTrigger data-testid="task-deal">
                  <SelectValue placeholder="Select deal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {deals.map((deal) => (
                    <SelectItem key={deal.id} value={deal.id}>{deal.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                data-testid="task-description"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="task-submit">
                {editingTask ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TaskSection({ title, tasks, variant, onToggle, onEdit, onDelete }) {
  const getPriorityBadge = (priority) => {
    const config = TASK_PRIORITIES.find(p => p.id === priority);
    return (
      <Badge variant="outline" className={config?.color}>
        <Flag size={12} className="mr-1" />
        {config?.label || priority}
      </Badge>
    );
  };

  const borderColor = variant === 'destructive' ? 'border-l-destructive' : 
                      variant === 'warning' ? 'border-l-warning' :
                      variant === 'muted' ? 'border-l-muted-foreground' : 'border-l-primary';

  return (
    <div>
      <h2 className="font-heading text-lg font-semibold mb-3">{title}</h2>
      <div className="space-y-2">
        {tasks.map((task) => (
          <div
            key={task.id}
            className={`bg-card border border-border border-l-4 ${borderColor} p-4 rounded-sm hover:-translate-y-[1px] transition-transform ${
              task.status === 'completed' ? 'opacity-60' : ''
            }`}
            data-testid={`task-card-${task.id}`}
          >
            <div className="flex items-start gap-3">
              <button
                onClick={() => onToggle(task)}
                className="mt-0.5"
                data-testid={`toggle-task-${task.id}`}
              >
                {task.status === 'completed' ? (
                  <CheckCircle size={20} className="text-success" weight="fill" />
                ) : (
                  <Circle size={20} className="text-muted-foreground" />
                )}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className={`font-medium ${task.status === 'completed' ? 'line-through' : ''}`}>
                    {task.title}
                  </h3>
                  {getPriorityBadge(task.priority)}
                </div>
                {task.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">{task.description}</p>
                )}
                {task.due_date && (
                  <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                    <Calendar size={12} />
                    <span>{formatDate(task.due_date)}</span>
                  </div>
                )}
              </div>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(task)}
                  data-testid={`edit-task-${task.id}`}
                >
                  <Pencil size={14} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(task.id)}
                  className="text-destructive hover:text-destructive"
                  data-testid={`delete-task-${task.id}`}
                >
                  <Trash size={14} />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
